import { imageUrl, type ImageSize } from '@/utils/image-url';
import { Image, type ImageProps } from 'expo-image';
import React from 'react';

/** expo-image props AppImage forwards unchanged. */
type PassthroughProps = Pick<
  ImageProps,
  | 'contentFit'
  | 'contentPosition'
  | 'cachePolicy'
  | 'recyclingKey'
  | 'placeholder'
  | 'placeholderContentFit'
  | 'transition'
  | 'priority'
  | 'onLoad'
  | 'onError'
  | 'style'
  | 'testID'
>;

/**
 * Either a ready-made expo-image `source`, or an R2 key (a full URL also
 * works; `imageUrl` handles both) plus a transform preset.
 */
type SourceProps =
  | { source: ImageProps['source']; r2Key?: never; preset?: never }
  | { r2Key: string | null | undefined; preset?: ImageSize; source?: never };

/** A label is required unless the image is purely decorative. */
type A11yProps =
  | { accessibilityLabel: string; decorative?: false }
  | { decorative: true; accessibilityLabel?: never };

export type AppImageProps = PassthroughProps & SourceProps & A11yProps;

/**
 * The app's single image primitive: a thin wrapper over expo-image `Image`
 * with the shared defaults (`memory-disk` cache, `cover` fit) and a required
 * accessibility decision.
 *
 * Size it via `style`, never `className`: NativeWind's className->style
 * interop does not reliably apply dimensions to expo-image, which collapses
 * the bitmap to zero size (see components/display/photo-grid.tsx).
 */
export function AppImage(props: AppImageProps) {
  const {
    source,
    r2Key,
    preset,
    decorative,
    accessibilityLabel,
    contentFit = 'cover',
    cachePolicy = 'memory-disk',
    contentPosition,
    recyclingKey,
    placeholder,
    placeholderContentFit,
    transition,
    priority,
    onLoad,
    onError,
    style,
    testID,
  } = props;

  const resolvedSource = source !== undefined ? source : resolveKeySource(r2Key, preset);
  const a11y = decorative
    ? {
        accessible: false,
        accessibilityElementsHidden: true,
        importantForAccessibility: 'no-hide-descendants' as const,
      }
    : { accessible: true, accessibilityRole: 'image' as const, accessibilityLabel };

  return (
    <Image
      source={resolvedSource}
      contentFit={contentFit}
      cachePolicy={cachePolicy}
      contentPosition={contentPosition}
      recyclingKey={recyclingKey}
      placeholder={placeholder}
      placeholderContentFit={placeholderContentFit}
      transition={transition}
      priority={priority}
      onLoad={onLoad}
      onError={onError}
      style={style}
      testID={testID}
      {...a11y}
    />
  );
}

function resolveKeySource(
  r2Key: string | null | undefined,
  preset: ImageSize | undefined,
): ImageProps['source'] {
  const uri = imageUrl(r2Key, preset);
  return uri ? { uri } : null;
}
